import PropTypes from 'prop-types';
import { useState } from 'react';
import ProviderSelector from './ProviderSelector';
import ProviderConnectorsSelector from './ProviderConnectorsSelector';
import { useTranslation } from 'react-i18next';
import { getProviderConnectors } from 'Services/providers';
import { trackError } from 'Utils/errorMonitoring';
import ProviderConnectionsConnector from './ProviderConnectionsConnector';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowLeft from 'Components/Atoms/OutlineIcons/OutlineArrowLeft';
import ItemSelector from 'Components/Molecules/ItemSelector/ItemSelector';
import Card from 'Components/Atoms/Card/Card';

const hasManualConnector = (connectors) => connectors.some((connector) => connector.service === 'manual');

const hasGroup = (connectors) => {
    if (hasManualConnector(connectors)) {
        return false;
    }
    return connectors.some((connector) => connector.group_name);
};

export default function ProviderConnectionCreate(props) {
    const { onConnect, onCancel } = props;

    const [providerSelected, setProviderSelected] = useState(null);
    const [connectorSelected, setConnectorSelected] = useState(null);
    const [groupConnectorsSelected, setGroupConnectorsSelected] = useState(null);
    const [groupConnectors, setGroupConnectors] = useState(null);
    const [connectors, setConnectors] = useState(null);
    const { t } = useTranslation('providers');

    const handleConnect = () => {
        onConnect();
    };

    const onSelectProvider = async (selected) => {
        setProviderSelected(selected);

        try {
            const connectors = await getProviderConnectors(selected.id);
            const filteredConnectors = connectors.filter((connector) => connector.is_enabled);

            setConnectors(filteredConnectors);

            if (hasManualConnector(filteredConnectors)) {
                setGroupConnectorsSelected(filteredConnectors);
                setConnectorSelected(filteredConnectors[0]);
                return;
            }

            if (!hasGroup(filteredConnectors)) {
                setGroupConnectorsSelected(filteredConnectors);
                return;
            }

            const connectorsWithoutGroup = filteredConnectors.filter((connector) => !connector.group_name);
            const connectorsWithGroup = filteredConnectors.filter((connector) => connector.group_name);

            const connectorsByGroup = connectorsWithGroup.reduce((acc, connector) => {
                if (!acc[connector.group_name]) {
                    acc[connector.group_name] = [];
                }

                acc[connector.group_name].push(connector);
                return acc;
            }, {});

            const connectorGroups = Object.values(connectorsByGroup).map((connectors) =>
                connectors.concat(connectorsWithoutGroup),
            );
            setGroupConnectors(connectorGroups);
        } catch (error) {
            trackError(error);
            setProviderSelected(null);
        }
    };

    if (!connectors) {
        return <ProviderSelector providerSelectedId={providerSelected?.id} onSelect={onSelectProvider} />;
    }

    if (!groupConnectorsSelected) {
        return (
            <>
                <div className="absolute top-0 left-0 md:top-2 md:left-2 z-30">
                    <IconButton
                        variant="ghost"
                        icon={<OutlineArrowLeft />}
                        onClick={() => {
                            setProviderSelected(null);
                            setConnectors(null);
                        }}
                    />
                </div>

                <div className="flex flex-col items-center justify-center">
                    <div className="text-center w- flex flex-col md:pt-8 lg:pt-20 items-center gap-2 pb-5 lg:pb-6">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 48 48">
                            <circle cx="24" cy="24" r="21" fill="#6050C6"></circle>
                            <path
                                fill="#fff"
                                d="M32.571 22.57A1.43 1.43 0 0 0 34 21.143V20.14c0-.526-.288-1.008-.752-1.258l-8.91-4.797a.71.71 0 0 0-.676 0l-8.91 4.797c-.463.25-.752.732-.752 1.258v1.002c0 .788.64 1.429 1.429 1.429h.714v7.143h-.714A1.43 1.43 0 0 0 14 31.142v1.429c0 .788.64 1.428 1.429 1.428H32.57A1.43 1.43 0 0 0 34 32.572v-1.429a1.43 1.43 0 0 0-1.429-1.428h-.714V22.57zM15.43 20.14 24 15.525l8.571 4.615v1.002H15.43zm9.285 9.574V22.57h2.143v7.143zm-3.571 0V22.57h2.143v7.143zM17.57 22.57h2.143v7.143h-2.143zm15.001 10H15.43v-1.429H32.57zm-2.143-2.857h-2.143V22.57h2.143z"
                            ></path>
                        </svg>
                        <h2 className="m-0 text-2xl md:text-3xl text-pretty font-semibold">{t('select-bank')}</h2>
                        <div className="text-gray-500 text-sm">{t('select-bank-description')}</div>
                    </div>
                    <div className="py-4 grid gap-3 w-full">
                        {groupConnectors.map((connectors) => {
                            return (
                                <ItemSelector
                                    key={connectors[0].id}
                                    title={connectors[0].group_name}
                                    image={providerSelected.logo_url}
                                    onClick={() => setGroupConnectorsSelected(connectors)}
                                />
                            );
                        })}
                    </div>
                </div>
            </>
        );
    }

    if (!connectorSelected) {
        return (
            <>
                <div className="absolute top-0 left-0 md:top-2 md:left-2 z-30">
                    <IconButton
                        variant="ghost"
                        icon={<OutlineArrowLeft />}
                        onClick={() => {
                            setGroupConnectorsSelected(null);
                            if (!hasGroup(connectors)) {
                                setConnectors(null);
                                setProviderSelected(null);
                                setGroupConnectors(null);
                            }
                        }}
                    />
                </div>

                <ProviderConnectorsSelector
                    connectors={groupConnectorsSelected}
                    onSelect={setConnectorSelected}
                    provider={providerSelected}
                />
            </>
        );
    }

    return (
        <>
            <div className="absolute top-0 left-0 md:top-2 md:left-2 z-30">
                <IconButton
                    variant="ghost"
                    icon={<OutlineArrowLeft />}
                    onClick={() => {
                        setConnectorSelected(null);
                    }}
                />
            </div>
            <div className="md:pt-8 lg:pt-16 pb-5 lg:pb-6">
                <Card>
                    <ProviderConnectionsConnector
                        connector={connectorSelected}
                        onConnect={handleConnect}
                        onError={() => {
                            setConnectorSelected(null);
                            if (connectors.length === 1) {
                                setConnectors(null);
                                setProviderSelected(null);
                            }
                        }}
                        onCancel={onCancel}
                    />
                </Card>
            </div>
        </>
    );
}

ProviderConnectionCreate.propTypes = {
    onConnect: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
